import React, { useEffect, useState } from 'react'
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Tooltip,
    Paper,
    Chip,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
} from '@mui/material'
import { DeleteRounded, Edit } from '@mui/icons-material'
import { toast } from 'react-toastify'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { tbdService } from '../../../services/admin/tbdService'
import AddEditTBDDialog from './components/AddEditTBDDialog'
import ConfirmationDialog from '../Workflow/components/ConfirmationDialog'

const getEmployeeInfo = (tbd) => {
    const employeeName = [tbd.employee_first_name, tbd.employee_last_name]
        .filter(Boolean)
        .join(' ')
        .trim() || tbd.user?.name || ''

    const employeeId = tbd.employee_id || tbd.user?.employee_id || ''

    return {
        employeeName,
        employeeId,
    }
}

const TBDManagement = () => {
    const { user } = useAuthContext()
    const [tbds, setTbds] = useState([])
    const [dropdownOptions, setDropdownOptions] = useState({
        designations: [],
        departments: [],
        subfunctions: [],
        users: [],
    })
    const [showDialog, setShowDialog] = useState(false)
    const [editingTbd, setEditingTbd] = useState(null)
    const [deletingTbd, setDeletingTbd] = useState(null)
    const [statusFilter, setStatusFilter] = useState('active')
    const [typeFilter, setTypeFilter] = useState('all')
    const [searchQuery, setSearchQuery] = useState('')

    const fetchTbds = async () => {
        if (!user) return
        try {
            const data = await tbdService.getTbds(user)
            setTbds(data.tbds || [])
            setDropdownOptions(data.dropdownOptions || {})
        } catch (error) {
            toast.error('Failed to fetch TBDs')
            console.error('Error fetching TBDs:', error)
        }
    }

    useEffect(() => {
        fetchTbds()
    }, [user])

    const handleAddNew = () => {
        setEditingTbd(null)
        setShowDialog(true)
    }

    const handleEdit = (tbd) => {
        if (tbd.is_dynamic) {
            toast.warning('Cannot edit dynamic TBD nodes')
            return
        }
        setEditingTbd(tbd)
        setShowDialog(true)
    }

    const handleDelete = async () => {
        if (!deletingTbd) return
        try {
            await tbdService.deleteTbd(user, deletingTbd.tbd_id)
            toast.success('TBD deleted successfully')
            setDeletingTbd(null)
            fetchTbds()
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to delete TBD')
        }
    }

    const handleSave = async (tbdData) => {
        try {
            await tbdService.createOrUpdateTbd(user, tbdData)
            toast.success(editingTbd ? 'TBD updated successfully' : 'TBD created successfully')
            setShowDialog(false)
            setEditingTbd(null)
            fetchTbds()
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to save TBD')
        }
    }

    const filteredTbds = tbds.filter((tbd) => {
        // Status filter - treat undefined/null status as 'active'
        const tbdStatus = tbd.status || 'active'
        if (statusFilter === 'active' && tbdStatus !== 'active') return false
        if (statusFilter === 'closed' && tbdStatus !== 'closed') return false

        // Type filter
        if (typeFilter !== 'all' && tbd.tbd_type !== typeFilter) return false

        // Search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase()
            return (
                tbd.tbd_id?.toLowerCase().includes(query) ||
                tbd.designation?.value?.toLowerCase().includes(query) ||
                tbd.department?.value?.toLowerCase().includes(query)
            )
        }

        return true
    })

    return (
        <Box>
            <Box style={{ marginBottom: 16 }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                    TBD Management
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Manage TBD (To Be Determined) nodes for organization planning
                </Typography>
            </Box>

            {/* Filters */}
            <Box display="flex" gap={2} mb={2}>
                <TextField
                    label="Search"
                    variant="outlined"
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by TBD ID, designation, department..."
                    sx={{ flex: 1 }}
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Status</InputLabel>
                    <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="closed">Closed</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Type</InputLabel>
                    <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} label="Type">
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="growth">Growth</MenuItem>
                        <MenuItem value="replacement">Replacement</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* Table */}
            <TableContainer component={Paper} style={{ backgroundColor: 'white' }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>TBD ID</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Designation</TableCell>
                            <TableCell>Department</TableCell>
                            <TableCell>Sub-function</TableCell>
                            <TableCell>Vacancy Type</TableCell>
                            <TableCell align="center">Positions</TableCell>
                            <TableCell>Employee Info</TableCell>
                            <TableCell>Reporting To</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredTbds.map((tbd) => {
                            const { employeeName, employeeId } = getEmployeeInfo(tbd)
                            const subFunctionValue =
                                tbd.sub_department?.value || tbd.subfunction?.value || '-'

                            return (
                            <TableRow key={tbd.tbd_id} hover>
                                <TableCell>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="body2">{tbd.tbd_id}</Typography>
                                        {tbd.is_dynamic && (
                                            <Chip label="Dynamic" size="small" color="info" variant="outlined" />
                                        )}
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={tbd.tbd_type || 'N/A'}
                                        size="small"
                                        color={tbd.tbd_type === 'growth' ? 'success' : 'warning'}
                                    />
                                </TableCell>
                                <TableCell>{tbd.designation?.value || '-'}</TableCell>
                                <TableCell>{tbd.department?.value || '-'}</TableCell>
                                <TableCell>{subFunctionValue}</TableCell>
                                <TableCell>{tbd.vacancy_type || '-'}</TableCell>
                                <TableCell align="center">{tbd.number_of_positions || 0}</TableCell>
                                <TableCell>
                                    {employeeName || employeeId ? (
                                        <Box>
                                            <Typography variant="body2">
                                                {employeeName || '-'}
                                            </Typography>
                                            {employeeId && (
                                                <Typography variant="caption" color="textSecondary">
                                                    ID: {employeeId}
                                                </Typography>
                                            )}
                                        </Box>
                                    ) : '-'}
                                </TableCell>
                                <TableCell>
                                    {tbd.reporting_to_user ? `${tbd.reporting_to_user.name}` : '-'}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={tbd.status || 'active'}
                                        size="small"
                                        color={tbd.status === 'active' ? 'success' : 'default'}
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <Box display="flex" justifyContent="center">
                                        <Tooltip title={tbd.is_dynamic ? 'Cannot edit dynamic TBD' : 'Edit TBD'}>
                                            <span>
                                                <IconButton
                                                    onClick={() => handleEdit(tbd)}
                                                    disabled={tbd.is_dynamic}
                                                    size="small"
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title={tbd.is_dynamic ? 'Cannot delete dynamic TBD' : 'Delete TBD'}>
                                            <span>
                                                <IconButton
                                                    onClick={() => setDeletingTbd(tbd)}
                                                    disabled={tbd.is_dynamic || tbd.status === 'closed'}
                                                    size="small"
                                                >
                                                    <DeleteRounded fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>
                            )
                        })}
                        {filteredTbds.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                                    <Typography variant="body2" color="textSecondary">
                                        No TBDs found
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Button
                variant="contained"
                color="primary"
                onClick={handleAddNew}
                style={{ marginTop: 16 }}
            >
                Add New TBD
            </Button>

            {/* Add/Edit Dialog */}
            <AddEditTBDDialog
                open={showDialog}
                onClose={() => {
                    setShowDialog(false)
                    setEditingTbd(null)
                }}
                onSave={handleSave}
                tbd={editingTbd}
                tbds={tbds}
                dropdownOptions={dropdownOptions}
            />

            {/* Delete Confirmation */}
            <ConfirmationDialog
                title={`Delete TBD '${deletingTbd?.tbd_id}'?`}
                message="This will soft delete the TBD node by setting its status to closed. It will no longer appear in active lists."
                open={deletingTbd != null}
                onClose={() => setDeletingTbd(null)}
                onConfirm={handleDelete}
                reloadProcesses={() => { }}
            />
        </Box>
    )
}

export default TBDManagement
